import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Plus, Trash2, Check, X, QrCode, LogOut, ExternalLink, RefreshCw, Copy, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  listContributors, createContributor, patchContributor, deleteContributor,
  listSubmissionsAdmin, patchSubmission, deleteSubmission,
  getToken, clearToken, me,
} from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';

export default function AdminContributors() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [authOk, setAuthOk] = useState(null);
  const [contributors, setContributors] = useState([]);
  const [subs, setSubs] = useState([]);
  const [newC, setNewC] = useState({ name: '', role: 'Board Member', photoUrl: '' });

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then(() => setAuthOk(true)).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([listContributors(), listSubmissionsAdmin()]);
      setContributors(c); setSubs(s);
    } catch (e) { toast({ title: 'Load failed', description: e.message }); }
  };
  useEffect(() => { if (authOk) load(); /* eslint-disable-line */ }, [authOk]);

  if (authOk === false) return <Navigate to="/admin/login" replace />;
  if (authOk === null) return null;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const contribUrl = (token) => `${origin}/contribute/${token}`;
  const qrSrc = (token) => `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(contribUrl(token))}&size=280x280&bgcolor=ffffff&color=0a1f44&margin=6`;

  const add = async (e) => {
    e.preventDefault();
    if (!newC.name.trim()) return;
    try {
      const c = await createContributor(newC);
      setContributors((prev) => [...prev, c]);
      setNewC({ name: '', role: 'Board Member', photoUrl: '' });
      toast({ title: 'Contributor added' });
    } catch (err) { toast({ title: 'Add failed', description: err.message }); }
  };

  const toggle = async (c) => {
    const updated = await patchContributor(c.id, { enabled: !c.enabled });
    setContributors((prev) => prev.map((x) => x.id === c.id ? updated : x));
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}? Their unique link will stop working immediately.`)) return;
    await deleteContributor(c.id);
    setContributors((prev) => prev.filter((x) => x.id !== c.id));
  };

  const copyLink = (t) => { navigator.clipboard?.writeText(contribUrl(t)); toast({ title: 'Link copied' }); };

  return (
    <div className="min-h-screen bg-[#f5f6f8] font-body">
      <div className="bg-[#0a1f44] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto object-contain" />
            <div>
              <div className="font-display text-xl tracking-wide">ADMIN CMS</div>
              <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">Contributor QR Codes &amp; Submissions</div>
            </div>
          </div>
          <button onClick={() => { clearToken(); nav('/admin/login'); }} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-4 py-2">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Add contributor */}
        <form onSubmit={add} className="bg-white shadow-sm p-6 grid md:grid-cols-2 lg:grid-cols-[1fr_1fr_1.4fr_auto] gap-3" data-testid="add-contributor-form">
          <input type="text" placeholder="Name (e.g. Coach Ricky Ricardo)" value={newC.name} onChange={(e) => setNewC({ ...newC, name: e.target.value })} data-testid="add-contrib-name" className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" />
          <select value={newC.role} onChange={(e) => setNewC({ ...newC, role: e.target.value })} data-testid="add-contrib-role" className="border border-[#0a1f44]/20 px-3 py-2.5 bg-white focus:outline-none focus:border-[#c8102e]">
            <option>Board Member</option>
            <option>President</option>
            <option>Vice President</option>
            <option>Head Coach</option>
            <option>Cheer Coordinator</option>
            <option>Team Mom</option>
            <option>Secretary</option>
            <option>Treasurer</option>
          </select>
          <input type="url" placeholder="Headshot URL (optional)" value={newC.photoUrl} onChange={(e) => setNewC({ ...newC, photoUrl: e.target.value })} data-testid="add-contrib-photo-url" className="border border-[#0a1f44]/20 px-3 py-2.5 focus:outline-none focus:border-[#c8102e]" />
          <button type="submit" data-testid="add-contrib-btn" className="inline-flex items-center gap-2 bg-[#c8102e] text-white font-heading uppercase tracking-widest text-xs px-6 py-2.5"><Plus className="w-4 h-4" /> Add</button>
        </form>

        {/* Contributors grid */}
        <div>
          <h2 className="font-display text-2xl text-[#0a1f44] mb-4">CONTRIBUTOR QR CODES</h2>
          {contributors.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-[#0a1f44]/15 text-[#0a1f44]/60" data-testid="contribs-empty">No contributors yet. Add one above to generate a QR.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contribs-grid">
              {contributors.map((c) => (
                <div key={c.id} data-testid={`contrib-card-${c.id}`} className={`bg-white shadow-sm p-4 border-t-4 ${c.enabled ? 'border-[#c8102e]' : 'border-[#0a1f44]/20'}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {c.photoUrl && <img src={c.photoUrl} alt={c.name} data-testid={`contrib-photo-${c.id}`} className="w-11 h-11 object-cover border border-[#0a1f44]/10 shrink-0" />}
                      <div className="min-w-0">
                      <div className="font-display text-lg text-[#0a1f44] leading-tight">{c.name}</div>
                      <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">{c.role}</div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => toggle(c)} data-testid={`toggle-${c.id}`} className={`p-1.5 border ${c.enabled ? 'border-emerald-500 text-emerald-700' : 'border-[#0a1f44]/30 text-[#0a1f44]/40'}`} title={c.enabled ? 'Disable' : 'Enable'}>
                        {c.enabled ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => remove(c)} data-testid={`remove-${c.id}`} className="p-1.5 border border-[#c8102e] text-[#c8102e]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <img src={qrSrc(c.token)} alt={`QR for ${c.name}`} className={`w-full h-auto max-w-[220px] mx-auto ${c.enabled ? '' : 'opacity-30'}`} />
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[10px] font-mono bg-[#0a1f44]/5 px-2 py-1.5 truncate">{contribUrl(c.token)}</code>
                      <button onClick={() => copyLink(c.token)} data-testid={`copy-link-${c.id}`} aria-label={`Copy contributor link for ${c.name}`} className="p-1.5 border border-[#0a1f44]/30 hover:border-[#c8102e]"><Copy className="w-3.5 h-3.5" /></button>
                      <a href={contribUrl(c.token)} target="_blank" rel="noreferrer" data-testid={`open-link-${c.id}`} aria-label={`Open contributor page for ${c.name}`} className="p-1.5 border border-[#0a1f44]/30 hover:border-[#c8102e]"><ExternalLink className="w-3.5 h-3.5" /></a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submissions queue */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-[#0a1f44]">SUBMISSIONS ({subs.length})</h2>
            <button onClick={load} data-testid="submissions-reload" className="text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reload</button>
          </div>
          {subs.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed border-[#0a1f44]/15 text-[#0a1f44]/60" data-testid="subs-empty">Nothing submitted yet.</div>
          ) : (
            <div className="space-y-3" data-testid="subs-list">
              {subs.map((s) => (
                <div key={s.id} data-testid={`sub-row-${s.id}`} className="bg-white shadow-sm p-4 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-heading uppercase tracking-widest px-2 py-0.5 ${s.status === 'approved' ? 'bg-emerald-500 text-white' : s.status === 'rejected' ? 'bg-[#0a1f44]/20 text-[#0a1f44]/70 line-through' : 'bg-[#c8102e] text-white'}`}>{s.status}</span>
                      <span className="text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/50">{s.type}</span>
                      <span className="text-[10px] text-[#0a1f44]/50">by {s.contributorName}</span>
                    </div>
                    <div className="font-display text-lg text-[#0a1f44]">{s.title}</div>
                    {s.description && <div className="text-sm text-[#0a1f44]/70 mt-1">{s.description}</div>}
                    <div className="text-xs text-[#0a1f44]/50 mt-1">{s.dateTime || '—'} · {s.location || '—'}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={async () => { const u = await patchSubmission(s.id, { status: 'approved' }); setSubs((p) => p.map((x) => x.id === s.id ? u : x)); }} data-testid={`approve-${s.id}`} className="p-2 bg-emerald-600 text-white" title="Approve"><Check className="w-4 h-4" /></button>
                    <button onClick={async () => { const u = await patchSubmission(s.id, { status: 'rejected' }); setSubs((p) => p.map((x) => x.id === s.id ? u : x)); }} data-testid={`reject-${s.id}`} className="p-2 border border-[#0a1f44]/30" title="Reject"><X className="w-4 h-4" /></button>
                    <button onClick={async () => { if (window.confirm('Delete?')) { await deleteSubmission(s.id); setSubs((p) => p.filter((x) => x.id !== s.id)); } }} data-testid={`delete-${s.id}`} className="p-2 border border-[#c8102e] text-[#c8102e]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
